import type { Edge, Node } from 'reactflow';
import { buildComplexDemoWorkflow, type DemoFlowNodeData, type DemoFlowTriggerData } from './complexDemoWorkflow';
import { createDefaultActionData } from './actionWorkflowTypes';
import { createAgentDataForKind } from './agentWorkflowTypes';
import { createDefaultFlowFxData } from './flowFxWorkflowTypes';
import { defaultLlmSettings, type McpStep } from './llmWorkflowTypes';
import { buildTriggerPreview, defaultTriggerSettings } from './triggerWorkflowTypes';

export type SampleWorkflowId =
  | 'enterprise-inbound'
  | 'llm-starter'
  | 'webhook-email'
  | 'agent-draft'
  | 'slack-notify'
  | 'http-callback'
  | 'event-llm'
  | 'scheduled-log'
  | 'emit-event'
  | 'action-logger'
  | 'transform-pipeline'
  | 'normalize-llm'
  | 'llm-mcp-orders'
  | 'data-analyst'
  | 'approval-router'
  | 'research-agent';

export type SampleWorkflowTemplate = {
  id: SampleWorkflowId;
  title: string;
  description: string;
  build: () => { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] };
};

function mk(
  id: string,
  source: string,
  sourceHandle: string | null,
  target: string,
  targetHandle: string | null
): Edge {
  return {
    id,
    source,
    target,
    type: 'smoothstep',
    ...(sourceHandle ? { sourceHandle } : {}),
    ...(targetHandle ? { targetHandle } : {}),
  };
}

function buildLlmStarterWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Manual run',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const llmData = {
    label: 'Assistant',
    paletteType: 'LLM_GPT4o',
    ...defaultLlmSettings('Assistant', 'LLM_GPT4o'),
    nodeName: 'assistant',
    operation: 'Generate' as const,
    parentPrompt: 'Help the user with their request.\n\n{{input}}\n',
    mcpSteps: [],
  };
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Done' });
  if (!end) {
    return { nodes: [], edges: [] };
  }
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sample-llm-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sample-llm-n', type: 'llmNode', position: { x: 420, y: 200 }, data: llmData },
    { id: 'sample-llm-e', type: 'flowFxNode', position: { x: 760, y: 220 }, data: end },
  ];
  const edges: Edge[] = [
    mk('sample-llm-e1', 'sample-llm-t', 'out', 'sample-llm-n', 'in'),
    mk('sample-llm-e2', 'sample-llm-n', 'out', 'sample-llm-e', 'in'),
  ];
  return { nodes, edges };
}

function buildWebhookEmailWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('webhook');
  if (settings.kind === 'webhook') {
    settings.path = '/webhooks/events/notify';
    settings.auth = 'apiKey';
  }
  const triggerData: DemoFlowTriggerData = {
    label: 'Inbound webhook',
    triggerKind: 'webhook',
    preview: buildTriggerPreview('webhook', settings),
    settings,
  };
  const action = createDefaultActionData({ type: 'Action_SendEmail', label: 'Send alert email' });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Complete' });
  if (!action || !end) {
    return { nodes: [], edges: [] };
  }
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sample-wh-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sample-wh-a', type: 'actionNode', position: { x: 440, y: 200 }, data: action },
    { id: 'sample-wh-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  const edges: Edge[] = [
    mk('sample-wh-e1', 'sample-wh-t', 'out', 'sample-wh-a', 'in'),
    mk('sample-wh-e2', 'sample-wh-a', 'out', 'sample-wh-e', 'in'),
  ];
  return { nodes, edges };
}

function buildAgentDraftWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Start',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const agent = createAgentDataForKind('email-composer', {
    type: 'Agent_EmailComposer',
    label: 'Email composer',
  });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'End' });
  if (!end) {
    return { nodes: [], edges: [] };
  }
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sample-ag-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sample-ag-a', type: 'agentNode', position: { x: 420, y: 180 }, data: agent },
    { id: 'sample-ag-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  const edges: Edge[] = [
    mk('sample-ag-e1', 'sample-ag-t', 'out', 'sample-ag-a', 'in'),
    mk('sample-ag-e2', 'sample-ag-a', 'out', 'sample-ag-e', 'in'),
  ];
  return { nodes, edges };
}

function buildSlackNotifyWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Run notification',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const slack = createDefaultActionData({ type: 'Action_SendSlack', label: 'Post to Slack' });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Done' });
  if (!slack || !end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-slack-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-slack-a', type: 'actionNode', position: { x: 420, y: 200 }, data: slack },
    { id: 'sw-slack-e', type: 'flowFxNode', position: { x: 760, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-slack-1', 'sw-slack-t', 'out', 'sw-slack-a', 'in'),
      mk('sw-slack-2', 'sw-slack-a', 'out', 'sw-slack-e', 'in'),
    ],
  };
}

function buildHttpCallbackWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('webhook');
  if (settings.kind === 'webhook') {
    settings.path = '/hooks/partner/callback';
    settings.auth = 'hmac';
  }
  const triggerData: DemoFlowTriggerData = {
    label: 'Partner webhook',
    triggerKind: 'webhook',
    preview: buildTriggerPreview('webhook', settings),
    settings,
  };
  const http = createDefaultActionData({ type: 'Action_HttpRequest', label: 'Forward payload' });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Complete' });
  if (!http || !end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-http-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-http-a', type: 'actionNode', position: { x: 440, y: 200 }, data: http },
    { id: 'sw-http-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-http-1', 'sw-http-t', 'out', 'sw-http-a', 'in'),
      mk('sw-http-2', 'sw-http-a', 'out', 'sw-http-e', 'in'),
    ],
  };
}

function buildEventLlmWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('event');
  if (settings.kind === 'event') settings.pattern = 'orders.*';
  const triggerData: DemoFlowTriggerData = {
    label: 'Order events',
    triggerKind: 'event',
    preview: buildTriggerPreview('event', settings),
    settings,
  };
  const llmData = {
    label: 'Event triage',
    paletteType: 'LLM_GPT4o',
    ...defaultLlmSettings('Event triage', 'LLM_GPT4o'),
    nodeName: 'event_triage',
    operation: 'Classify' as const,
    parentPrompt: 'Classify the domain event and suggest next action.\n\n{{ trigger.payload }}\n',
    mcpSteps: [],
  };
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'End' });
  if (!end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-ev-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-ev-l', type: 'llmNode', position: { x: 420, y: 200 }, data: llmData },
    { id: 'sw-ev-e', type: 'flowFxNode', position: { x: 760, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-ev-1', 'sw-ev-t', 'out', 'sw-ev-l', 'in'),
      mk('sw-ev-2', 'sw-ev-l', 'out', 'sw-ev-e', 'in'),
    ],
  };
}

function buildScheduledLogWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('scheduled');
  const triggerData: DemoFlowTriggerData = {
    label: 'Daily job',
    triggerKind: 'scheduled',
    preview: buildTriggerPreview('scheduled', settings),
    settings,
  };
  const log = createDefaultActionData({ type: 'Action_Logger', label: 'Job heartbeat' });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'OK' });
  if (!log || !end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-sc-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-sc-a', type: 'actionNode', position: { x: 440, y: 200 }, data: log },
    { id: 'sw-sc-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-sc-1', 'sw-sc-t', 'out', 'sw-sc-a', 'in'),
      mk('sw-sc-2', 'sw-sc-a', 'out', 'sw-sc-e', 'in'),
    ],
  };
}

function buildEmitEventWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Publish run',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const emit = createDefaultActionData({ type: 'Action_EmitEvent', label: 'Emit domain event' });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Done' });
  if (!emit || !end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-em-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-em-a', type: 'actionNode', position: { x: 440, y: 200 }, data: emit },
    { id: 'sw-em-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-em-1', 'sw-em-t', 'out', 'sw-em-a', 'in'),
      mk('sw-em-2', 'sw-em-a', 'out', 'sw-em-e', 'in'),
    ],
  };
}

function buildActionLoggerWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Debug run',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const log = createDefaultActionData({ type: 'Action_Logger', label: 'Trace payload' });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'End' });
  if (!log || !end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-lg-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-lg-a', type: 'actionNode', position: { x: 440, y: 200 }, data: log },
    { id: 'sw-lg-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-lg-1', 'sw-lg-t', 'out', 'sw-lg-a', 'in'),
      mk('sw-lg-2', 'sw-lg-a', 'out', 'sw-lg-e', 'in'),
    ],
  };
}

function buildTransformPipelineWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Start',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const xf = createDefaultFlowFxData({ type: 'Util_TransformData', label: 'Shape payload' });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Complete' });
  if (!xf || !end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-xf-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-xf-x', type: 'flowFxNode', position: { x: 400, y: 200 }, data: xf },
    { id: 'sw-xf-e', type: 'flowFxNode', position: { x: 740, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-xf-1', 'sw-xf-t', 'out', 'sw-xf-x', 'in'),
      mk('sw-xf-2', 'sw-xf-x', 'out', 'sw-xf-e', 'in'),
    ],
  };
}

function buildNormalizeLlmWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Start',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const setVar = createDefaultFlowFxData({ type: 'Util_SetVariable', label: 'Normalize fields' });
  const llmData = {
    label: 'Reason over context',
    paletteType: 'LLM_ClaudeSonnet',
    ...defaultLlmSettings('Reason over context', 'LLM_ClaudeSonnet'),
    nodeName: 'reasoner',
    operation: 'Analyze' as const,
    parentPrompt: 'Use normalized variables.\n\n{{ normalize_fields.source }}\n',
    mcpSteps: [],
  };
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'End' });
  if (!setVar || !end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-nm-t', type: 'triggerNode', position: { x: 40, y: 220 }, data: triggerData },
    { id: 'sw-nm-s', type: 'flowFxNode', position: { x: 360, y: 200 }, data: setVar },
    { id: 'sw-nm-l', type: 'llmNode', position: { x: 680, y: 180 }, data: llmData },
    { id: 'sw-nm-e', type: 'flowFxNode', position: { x: 1000, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-nm-1', 'sw-nm-t', 'out', 'sw-nm-s', 'in'),
      mk('sw-nm-2', 'sw-nm-s', 'out', 'sw-nm-l', 'in'),
      mk('sw-nm-3', 'sw-nm-l', 'out', 'sw-nm-e', 'in'),
    ],
  };
}

function buildLlmMcpOrdersWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Start',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const mcpStep: McpStep = {
    id: 'sw-mcp-orders',
    kind: 'database',
    label: 'Orders',
    outputKey: 'open_orders',
    paletteType: 'MCP_SalesDB_Orders',
    database: {
      table: 'orders',
      query: 'status = {{ input.status }}',
      operation: 'read',
    },
  };
  const llmData = {
    label: 'Order assistant',
    paletteType: 'LLM_GPT4o',
    ...defaultLlmSettings('Order assistant', 'LLM_GPT4o'),
    nodeName: 'order_bot',
    operation: 'Extract' as const,
    parentPrompt: 'Summarize order lines using MCP context.\n\n{{input}}\n',
    mcpSteps: [mcpStep],
  };
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Done' });
  if (!end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-mcp-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-mcp-l', type: 'llmNode', position: { x: 420, y: 180 }, data: llmData },
    { id: 'sw-mcp-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-mcp-1', 'sw-mcp-t', 'out', 'sw-mcp-l', 'in'),
      mk('sw-mcp-2', 'sw-mcp-l', 'out', 'sw-mcp-e', 'in'),
    ],
  };
}

function buildDataAnalystAgentWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Analyze',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const agent = createAgentDataForKind('data-analyst', {
    type: 'Agent_DataAnalyst',
    label: 'Data analyst',
  });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'End' });
  if (!end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-da-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-da-a', type: 'agentNode', position: { x: 420, y: 180 }, data: agent },
    { id: 'sw-da-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-da-1', 'sw-da-t', 'out', 'sw-da-a', 'in'),
      mk('sw-da-2', 'sw-da-a', 'out', 'sw-da-e', 'in'),
    ],
  };
}

function buildApprovalRouterAgentWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('webhook');
  if (settings.kind === 'webhook') {
    settings.path = '/approvals/inbox';
    settings.auth = 'none';
  }
  const triggerData: DemoFlowTriggerData = {
    label: 'Approval request',
    triggerKind: 'webhook',
    preview: buildTriggerPreview('webhook', settings),
    settings,
  };
  const agent = createAgentDataForKind('approval-router', {
    type: 'Agent_ApprovalRouter',
    label: 'Policy router',
  });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'Routed' });
  if (!end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-ap-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-ap-a', type: 'agentNode', position: { x: 440, y: 180 }, data: agent },
    { id: 'sw-ap-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-ap-1', 'sw-ap-t', 'out', 'sw-ap-a', 'in'),
      mk('sw-ap-2', 'sw-ap-a', 'out', 'sw-ap-e', 'in'),
    ],
  };
}

function buildResearchAgentWorkflow(): { nodes: Node<DemoFlowNodeData>[]; edges: Edge[] } {
  const settings = defaultTriggerSettings('manual');
  const triggerData: DemoFlowTriggerData = {
    label: 'Research task',
    triggerKind: 'manual',
    preview: buildTriggerPreview('manual', settings),
    settings,
  };
  const agent = createAgentDataForKind('research-agent', {
    type: 'Agent_DataResearcher',
    label: 'Research agent',
  });
  const end = createDefaultFlowFxData({ type: 'Flow_End', label: 'End' });
  if (!end) return { nodes: [], edges: [] };
  const nodes: Node<DemoFlowNodeData>[] = [
    { id: 'sw-rs-t', type: 'triggerNode', position: { x: 80, y: 220 }, data: triggerData },
    { id: 'sw-rs-a', type: 'agentNode', position: { x: 420, y: 180 }, data: agent },
    { id: 'sw-rs-e', type: 'flowFxNode', position: { x: 780, y: 220 }, data: end },
  ];
  return {
    nodes,
    edges: [
      mk('sw-rs-1', 'sw-rs-t', 'out', 'sw-rs-a', 'in'),
      mk('sw-rs-2', 'sw-rs-a', 'out', 'sw-rs-e', 'in'),
    ],
  };
}

export const SAMPLE_WORKFLOW_CATALOG: SampleWorkflowTemplate[] = [
  {
    id: 'enterprise-inbound',
    title: 'Enterprise inbound lead',
    description: 'Full demo: webhook enrichment, branching, parallel CRM paths, merge, and audit log.',
    build: () => buildComplexDemoWorkflow(),
  },
  {
    id: 'llm-starter',
    title: 'LLM starter',
    description: 'Manual trigger, a single GPT-style assistant node, and a clean end node.',
    build: buildLlmStarterWorkflow,
  },
  {
    id: 'webhook-email',
    title: 'Webhook to email',
    description: 'Receive a webhook, send a templated email, then finish the run.',
    build: buildWebhookEmailWorkflow,
  },
  {
    id: 'agent-draft',
    title: 'Agent email draft',
    description: 'Manual start, email-composer agent with default ops, then end.',
    build: buildAgentDraftWorkflow,
  },
  {
    id: 'slack-notify',
    title: 'Slack notify',
    description: 'Manual run, post a Slack message, then end the workflow.',
    build: buildSlackNotifyWorkflow,
  },
  {
    id: 'http-callback',
    title: 'HTTP callback',
    description: 'Webhook receives a payload, forwards it with an HTTP request, then completes.',
    build: buildHttpCallbackWorkflow,
  },
  {
    id: 'event-llm',
    title: 'Event-driven LLM',
    description: 'Domain event trigger, LLM classifies the payload, then finishes.',
    build: buildEventLlmWorkflow,
  },
  {
    id: 'scheduled-log',
    title: 'Scheduled heartbeat',
    description: 'Cron-style schedule fires an action logger step, then ends.',
    build: buildScheduledLogWorkflow,
  },
  {
    id: 'emit-event',
    title: 'Emit bus event',
    description: 'Manual start, emit an integration event, then close the run.',
    build: buildEmitEventWorkflow,
  },
  {
    id: 'action-logger',
    title: 'Action logger',
    description: 'Manual debug path using the logger action before the end node.',
    build: buildActionLoggerWorkflow,
  },
  {
    id: 'transform-pipeline',
    title: 'Transform pipeline',
    description: 'Manual trigger, transform utility node, then a clean end.',
    build: buildTransformPipelineWorkflow,
  },
  {
    id: 'normalize-llm',
    title: 'Normalize then LLM',
    description: 'Set variables, then a Sonnet node reasons over normalized context.',
    build: buildNormalizeLlmWorkflow,
  },
  {
    id: 'llm-mcp-orders',
    title: 'LLM + MCP orders',
    description: 'Assistant with a Sales DB orders MCP step, then end.',
    build: buildLlmMcpOrdersWorkflow,
  },
  {
    id: 'data-analyst',
    title: 'Data analyst agent',
    description: 'Manual start, data-analyst agent template, then end.',
    build: buildDataAnalystAgentWorkflow,
  },
  {
    id: 'approval-router',
    title: 'Approval router',
    description: 'Webhook intake, approval-router agent, then completion.',
    build: buildApprovalRouterAgentWorkflow,
  },
  {
    id: 'research-agent',
    title: 'Research agent',
    description: 'Manual kickoff, researcher agent with default op ladder, then end.',
    build: buildResearchAgentWorkflow,
  },
];
